import React, { forwardRef } from 'react';
import { Button, Cascader, DatePicker, Form, Image, Input, InputNumber, Radio, Select, Space } from 'antd';
import locale from 'antd/locale/zh_CN';
import { CloseOutlined } from '@ant-design/icons';
import Config from '../../../config/Config';
import WidgetPositionDate from '../../../widget/WidgetPositionDate';
import ImageUploader from '../ImageUploader';

const filePath = Config.getFilePath();
const temporaryFile = Config.getTemporaryFilePath();

const renderImageList = ({ applicantId, value, onRemove }) => {
  const attachments = Array.isArray(value) ? value : (value && value.attachments) || [];
  return attachments.map((item, index) => {
    const imageId = typeof item === 'string' ? item : item && item.objId;
    if (!imageId) return null;

    const src = typeof item === 'string'
      ? `${filePath}applicant/${applicantId}/L${imageId}.png`
      : `${temporaryFile}upload/applicant/L${imageId}.png`;

    return (
      <div className="resume-new-upload-thumb" key={`${imageId}-${index}`}>
        <Image width={75} src={src} />
        <CloseOutlined onClick={() => onRemove(index)} />
      </div>
    );
  });
};

const getFieldOptions = (field, options) => (field.optionKey ? options[field.optionKey] : field.options);

/**
 * 把后端的"开始/结束"字段对(OCCFROM/OCCTO、ENRFROM/ENRTO 等)转成
 * WidgetPositionDate 期望的 [YYYY-MM, YYYY-MM] 数组,用于编辑态回显。
 *
 * 之所以需要这个 helper:
 *   - 后端返回的是 OCCFROM/OCCTO 这种"开始/结束"两个字段;
 *   - 但 formList 声明的字段名是 OCCDATE 这种"区间"字段,提交时由
 *     buildMonthRangeParams(values, 'OCCDATE', 'OCCFROM', 'OCCTO') 拆回去;
 *   - 主页面按设计约定**不归一化** raw 数据,所以 initialValues 里**没有**
 *     OCCDATE,只可能有 OCCFROM/OCCTO;直接 initialValues[field.props] 永远是
 *     undefined,这就是时间字段不回显的根因。
 *
 * 由 sectionConfig.formList 里 customDate 字段的 fromProps / toProps 提供映射,
 * 这里把字符串日期(YYYY-MM-DD 或 YYYY-MM)裁剪到月份粒度,WidgetPositionDate
 * 用的是 picker='month'。
 *
 * @param {object} initialValues 表单初始值(来自 ListSectionView 传入的 record)
 * @param {object} field        当前 customDate 字段声明,含 props / fromProps / toProps
 * @returns {[string|null, string|null]|undefined}
 *   - 能构造出范围 → 返回 [from, to]
 *   - 构造不出来   → 返回 undefined,让调用方 fallback 到空数组
 */
const resolveCustomDateDefault = (initialValues, field) => {
  if (!initialValues) return undefined;

  // 主路径: initialValues[field.props] 已经是 [from, to] 形态(走归一化流程的场景)
  const direct = initialValues[field.props];
  if (Array.isArray(direct) && direct.length > 0) return direct;

  // Fallback: 后端原始数据只有 OCCFROM/OCCTO 这种"开始/结束"两个字段
  // (按当前设计,主页面不归一化 raw 数据,所以 initialValues 里**没有**
  //  OCCDATE/ENRDATE,只可能有 OCCFROM/OCCTO / ENRFROM/ENRTO)
  if (!field.fromProps && !field.toProps) return undefined;

  const fromRaw = field.fromProps ? initialValues[field.fromProps] : undefined;
  const toRaw = field.toProps ? initialValues[field.toProps] : undefined;
  // 后端返回 'YYYY-MM-DD'(如 '2026-02-09'),WidgetPositionDate 用 picker='month'
  // + dateFormat='YYYY-MM',所以裁到月份粒度
  const from = fromRaw ? String(fromRaw).slice(0, 7) : null;
  // 单边缺失(如只有 OCCFROM、没有 OCCTO)表示"至今" — 也要构造,不能丢
  const to = toRaw ? String(toRaw).slice(0, 7) : null;

  if (from || to) return [from, to];
  return undefined;
};

const ResumeEditorForm = forwardRef(({
  section,
  initialValues,
  options,
  applicantId,
  certificateFile,
  setCertificateFile,
  worksFile,
  setWorksFile,
  onTrigger,
  onSubmit,
  onCancel,
}, ref) => {
  const renderField = (field, index) => {
    if (field.type === 'tips') return <div className="resume-new-form-tips">{field.text}</div>;
    if (field.type === 'input') return <Input disabled={field.disabled} placeholder={field.placeholder || '请输入'} />;
    if (field.type === 'inputNumber') return <InputNumber min={0} placeholder={field.placeholder || '请输入'} style={{ width: 200 }} />;
    if (field.type === 'textarea') return <Input.TextArea rows={5} showCount maxLength={1000} placeholder={field.placeholder || '请输入'} />;
    if (field.type === 'select') return <Select placeholder={field.placeholder || '请选择'} options={getFieldOptions(field, options)} />;
    if (field.type === 'radio') return <Radio.Group block optionType="button" buttonStyle="solid" options={getFieldOptions(field, options)} />;
    if (field.type === 'cascader') return <Cascader options={options[field.optionKey] || []} fieldNames={{ label: 'name', value: 'code' }} placeholder={field.placeholder || '请选择'} />;
    if (field.type === 'DatePicker') return <DatePicker locale={locale} format="YYYY-MM-DD" placeholder={field.placeholder || '请选择日期'} showToday={false} />;
    if (field.type === 'customDate') {
      const now = new Date();
      const maxMonth = `${now.getFullYear()}-${now.getMonth() + 1 > 9 ? now.getMonth() + 1 : `0${now.getMonth() + 1}`}`;
      const defaultValue = resolveCustomDateDefault(initialValues, field);
      return <WidgetPositionDate picker="month" maxDate={maxMonth} dateFormat="YYYY-MM" defaultValue={defaultValue || []} options={{ type: 'range' }} />;
    }
    if (field.type === 'inputBlur') {
      return <Input readOnly placeholder={field.placeholder || '请选择'} onClick={() => onTrigger(field, index)} />;
    }
    if (field.type === 'inputNumbers') {
      return (
        <Space>
          <Form.Item name={field.props} noStyle><InputNumber min={0} placeholder={field.placeholder || '请输入'} style={{ width: 150 }} /></Form.Item>
          -
          <Form.Item name={field.moreProps} noStyle><InputNumber min={0} placeholder={field.placeholder || '请输入'} style={{ width: 150 }} /></Form.Item>
          {field.unit || null}
        </Space>
      );
    }
    if (field.type === 'certificateFile') {
      return (
        <div className="resume-new-upload-row">
          {certificateFile && certificateFile.objId ? renderImageList({ applicantId, value: [certificateFile], onRemove: () => setCertificateFile(undefined) }) : null}
          {certificateFile && certificateFile.attachments ? renderImageList({ applicantId, value: certificateFile, onRemove: () => setCertificateFile({ ...certificateFile, attachments: [] }) }) : null}
          <ImageUploader onChange={(value) => setCertificateFile(value)} />
        </div>
      );
    }
    if (field.type === 'worksFile') {
      const value = worksFile || [];
      const attachments = Array.isArray(value) ? value : value.attachments || [];
      return (
        <div className="resume-new-upload-row">
          {renderImageList({
            applicantId,
            value,
            onRemove: (removeIndex) => {
              const next = attachments.filter((_, itemIndex) => itemIndex !== removeIndex);
              setWorksFile(Array.isArray(value) ? next : { ...value, attachments: next });
            },
          })}
          {attachments.length < 10 ? <ImageUploader onChange={(uploaded) => setWorksFile(Array.isArray(value) ? [...attachments, uploaded] : { ...value, attachments: [...attachments, uploaded] })} /> : null}
        </div>
      );
    }
    return null;
  };

  const renderFormItem = (field, index) => {
    if (field.type === 'tips') {
      return <div key={field.props || index} className="resume-new-form-full">{renderField(field, index)}</div>;
    }

    if (field.type === 'inputNumbers') {
      return (
        <Form.Item key={field.props} label={field.label}>
          {renderField(field, index)}
        </Form.Item>
      );
    }

    return (
      <React.Fragment key={field.props}>
        {field.hiddenValueProp ? <Form.Item name={field.hiddenValueProp} hidden><Input /></Form.Item> : null}
        <Form.Item
          label={field.label}
          name={field.props}
          rules={field.required ? [{ required: true, message: field.message || `${field.label}不能为空` }] : undefined}
        >
          {renderField(field, index)}
        </Form.Item>
      </React.Fragment>
    );
  };

  return (
    <Form ref={ref} onFinish={onSubmit} initialValues={initialValues} {...section.formProps} className="resume-new-form">
      <div className="resume-new-form-grid">
        {section.formList.map(renderFormItem)}
      </div>
      <div className="resume-new-form-actions">
        <Button onClick={onCancel}>取消</Button>
        <Button type="primary" htmlType="submit">保存</Button>
      </div>
    </Form>
  );
});

export default ResumeEditorForm;
